/**
 * RealTimeConversionFraudDetectionPage — Trackier-style real-time fraud console.
 *
 * Operator view on top of /api/v1/real-time-conversion-fraud-detection.
 *
 * Layout (single viewport):
 *   1. Topbar — page title + live pulse dot + refetch cadence + total count.
 *   2. Four KPI tiles — flagged-last-min, duplicate-rate, chargeback-risk,
 *      blocked-revenue. Derived locally from the latest rows if the BE does
 *      not emit a summary block.
 *   3. Reason chips — dup-txn / geo-spoof / rapid-cvr / ip-sharing +
 *      ua-anomaly + chargeback-risk. Click to filter the feed.
 *   4. Live feed table — ts, conv_id, advertiser, reason, score, status.
 *      Loading / empty / error states are all first-class.
 *
 * Brand: emerald/green accents, red-600 ONLY for fraud scores + blocked
 * statuses. White / zinc-200 / zinc-900 surfaces, Inter, 6-8px rounding,
 * inline SVG icons. No emoji, no dark mode, no competitor names.
 */

import { useMemo, useState } from "react";
import {
	type FraudReason,
	type FraudStatus,
	type RealTimeConversionFraudDetectionRow,
	REASON_LABEL,
	STATUS_LABEL,
	deriveTiles,
	formatTs,
	formatUsd,
	isFraudulent,
	scoreBucket,
} from "./api";
import {
	REFETCH_INTERVAL_MS,
	useRealTimeConversionFraudDetectionQuery,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — kept inline so this is a single owned file.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.06)]";
const TH =
	"px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900 tabular-nums";

const CHIPS: { key: FraudReason; tone: ChipTone }[] = [
	{ key: "dup_txn", tone: "emerald" },
	{ key: "geo_spoof", tone: "emerald" },
	{ key: "rapid_cvr", tone: "emerald" },
	{ key: "ip_sharing", tone: "emerald" },
	{ key: "ua_anomaly", tone: "zinc" },
	{ key: "chargeback_risk", tone: "red" },
];

type ChipTone = "emerald" | "zinc" | "red";

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function RealTimeConversionFraudDetectionPage() {
	const query = useRealTimeConversionFraudDetectionQuery();
	const [reasonFilter, setReasonFilter] = useState<FraudReason | null>(null);

	const items = query.data?.items ?? [];
	const summary = query.data?.summary;

	const tiles = useMemo(() => deriveTiles(items, summary), [items, summary]);

	const visible = useMemo(() => {
		if (!reasonFilter) return items;
		return items.filter((r) => r.reason === reasonFilter);
	}, [items, reasonFilter]);

	const count = query.data?.count ?? items.length;

	return (
		<main
			data-testid="real-time-conversion-fraud-detection-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8 sm:py-10"
		>
			<Topbar
				live={query.isFetching}
				refetchMs={REFETCH_INTERVAL_MS}
				count={count}
				errored={Boolean(query.error)}
			/>

			<TilesRow tiles={tiles} />

			<ChipRow
				active={reasonFilter}
				onSelect={(k) => setReasonFilter((prev) => (prev === k ? null : k))}
				rows={items}
			/>

			<FeedCard
				rows={visible}
				totalRows={items.length}
				loading={query.isLoading}
				error={query.error ?? null}
				filtered={Boolean(reasonFilter)}
			/>
		</main>
	);
}

// ---------------------------------------------------------------------------
// Topbar
// ---------------------------------------------------------------------------

function Topbar(props: {
	live: boolean;
	refetchMs: number;
	count: number;
	errored: boolean;
}) {
	const { live, refetchMs, count, errored } = props;
	const seconds = Math.round(refetchMs / 1000);

	return (
		<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
			<div className="flex flex-col gap-1.5">
				<p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
					Fraud Operations
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
					Real-Time Conversion Fraud
				</h1>
				<p className="max-w-prose text-sm text-zinc-600">
					Live stream of incoming conversions scored by the edge fraud
					engine. Flagged and blocked events arrive within seconds of the
					postback — no page refresh needed.
				</p>
			</div>
			<div
				className="flex flex-wrap items-center gap-3 text-sm text-zinc-600"
				aria-live="polite"
			>
				<span
					className="inline-flex items-center gap-2 rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700"
					data-testid="live-indicator"
					data-live={live ? "true" : "false"}
				>
					<LivePulse active={live} errored={errored} />
					{errored ? "Offline" : live ? "Live" : "Idle"}
					<span className="text-zinc-400" aria-hidden>
						&middot;
					</span>
					<span className="text-zinc-500">{seconds}s refresh</span>
				</span>
				<span className="inline-flex items-center gap-2 rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700">
					<ShieldIcon />
					<span className="tabular-nums">{count.toLocaleString()}</span>
					<span className="text-zinc-500">events</span>
				</span>
			</div>
		</header>
	);
}

function LivePulse(props: { active: boolean; errored: boolean }) {
	if (props.errored) {
		return (
			<span
				aria-hidden
				className="inline-block h-2.5 w-2.5 rounded-full bg-red-600"
			/>
		);
	}
	return (
		<span aria-hidden className="relative inline-flex h-2.5 w-2.5">
			{props.active && (
				<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
			)}
			<span
				className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
					props.active ? "bg-green-500" : "bg-zinc-300"
				}`}
			/>
		</span>
	);
}

// ---------------------------------------------------------------------------
// Tile row — 4 KPIs
// ---------------------------------------------------------------------------

function TilesRow(props: {
	tiles: ReturnType<typeof deriveTiles>;
}) {
	const { tiles } = props;
	return (
		<section
			className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
			aria-label="KPI tiles"
			data-testid="tiles-row"
		>
			<Tile
				label="Flagged · last 60s"
				value={tiles.flagged_last_min.toLocaleString()}
				tone="emerald"
				icon={<FlagIcon />}
				testid="tile-flagged-last-min"
			/>
			<Tile
				label="Duplicate rate"
				value={formatPercent(tiles.duplicate_rate)}
				tone="emerald"
				icon={<CopyIcon />}
				testid="tile-duplicate-rate"
			/>
			<Tile
				label="Chargeback risk"
				value={formatPercent(tiles.chargeback_risk)}
				tone="emerald"
				icon={<AlertIcon />}
				testid="tile-chargeback-risk"
			/>
			<Tile
				label="Blocked revenue"
				value={formatUsd(tiles.blocked_revenue_cents)}
				tone="emerald"
				icon={<ShieldIcon />}
				testid="tile-blocked-revenue"
			/>
		</section>
	);
}

function Tile(props: {
	label: string;
	value: string;
	tone: "emerald";
	icon: React.ReactNode;
	testid: string;
}) {
	return (
		<article
			className={`${CARD} flex items-start gap-4 p-5`}
			data-testid={props.testid}
		>
			<span
				className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-emerald-200 bg-emerald-50 text-emerald-700"
				aria-hidden
			>
				{props.icon}
			</span>
			<div className="flex min-w-0 flex-col gap-1">
				<p className="truncate text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">
					{props.label}
				</p>
				<p className="text-2xl font-semibold tabular-nums text-zinc-900">
					{props.value}
				</p>
			</div>
		</article>
	);
}

function formatPercent(ratio: number): string {
	if (!Number.isFinite(ratio)) return "0.0%";
	const pct = Math.max(0, Math.min(1, ratio)) * 100;
	return `${pct.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// Reason chips filter
// ---------------------------------------------------------------------------

function ChipRow(props: {
	active: FraudReason | null;
	onSelect: (k: FraudReason) => void;
	rows: RealTimeConversionFraudDetectionRow[];
}) {
	const counts = useMemo(() => {
		const c: Record<FraudReason, number> = {
			dup_txn: 0,
			geo_spoof: 0,
			rapid_cvr: 0,
			ip_sharing: 0,
			ua_anomaly: 0,
			chargeback_risk: 0,
			unknown: 0,
		};
		for (const r of props.rows) {
			if (r.reason && r.reason in c) {
				c[r.reason] += 1;
			}
		}
		return c;
	}, [props.rows]);

	return (
		<section
			className="flex flex-wrap items-center gap-2"
			aria-label="Reason filters"
			data-testid="chip-row"
		>
			{CHIPS.map((chip) => {
				const active = props.active === chip.key;
				const count = counts[chip.key];
				return (
					<button
						key={chip.key}
						type="button"
						onClick={() => props.onSelect(chip.key)}
						aria-pressed={active}
						data-testid={`chip-${chip.key}`}
						className={chipClasses(chip.tone, active)}
					>
						<span>{REASON_LABEL[chip.key]}</span>
						<span
							className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold tabular-nums ${
								active
									? "bg-white/70 text-zinc-700"
									: "bg-zinc-100 text-zinc-600"
							}`}
						>
							{count}
						</span>
					</button>
				);
			})}
			{props.active && (
				<button
					type="button"
					onClick={() => props.onSelect(props.active as FraudReason)}
					className="ml-1 text-xs text-zinc-500 underline-offset-4 hover:underline"
					data-testid="chip-clear"
				>
					Clear filter
				</button>
			)}
		</section>
	);
}

function chipClasses(tone: ChipTone, active: boolean): string {
	const base =
		"inline-flex items-center gap-2 rounded-[6px] border px-3 py-1.5 text-xs font-medium transition-colors";
	if (active) {
		switch (tone) {
			case "emerald":
				return `${base} border-emerald-500 bg-emerald-50 text-emerald-700`;
			case "red":
				return `${base} border-red-500 bg-red-50 text-red-700`;
			case "zinc":
				return `${base} border-zinc-400 bg-zinc-100 text-zinc-800`;
		}
	}
	return `${base} border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50`;
}

// ---------------------------------------------------------------------------
// Live feed
// ---------------------------------------------------------------------------

function FeedCard(props: {
	rows: RealTimeConversionFraudDetectionRow[];
	totalRows: number;
	loading: boolean;
	error: Error | null;
	filtered: boolean;
}) {
	const { rows, totalRows, loading, error, filtered } = props;

	return (
		<section className={`${CARD} flex flex-col`} aria-label="Live feed">
			<header className="flex flex-col gap-1 border-b border-zinc-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex flex-col gap-1">
					<h2 className="text-base font-semibold text-zinc-900">
						Live feed
					</h2>
					<p className="text-xs text-zinc-500">
						Most recent conversions, newest first. Scores ≥ 60 render red.
					</p>
				</div>
				<span className="text-xs text-zinc-500">
					{filtered
						? `${rows.length.toLocaleString()} of ${totalRows.toLocaleString()}`
						: `${totalRows.toLocaleString()} total`}
				</span>
			</header>

			{error ? (
				<div className="px-5 py-6" role="alert">
					<p
						className="text-sm text-red-600"
						data-testid="real-time-conversion-fraud-detection-error"
					>
						{error.message}
					</p>
				</div>
			) : loading ? (
				<div className="px-5 py-6">
					<p
						className="text-sm text-zinc-500"
						data-testid="real-time-conversion-fraud-detection-loading"
					>
						Loading live feed.
					</p>
				</div>
			) : rows.length === 0 ? (
				<div className="px-5 py-10 text-center">
					<p
						className="text-sm text-zinc-500"
						data-testid="real-time-conversion-fraud-detection-empty"
					>
						{filtered
							? "No conversions match that reason right now."
							: "No suspicious conversions in the current window."}
					</p>
				</div>
			) : (
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse"
						data-testid="real-time-conversion-fraud-detection-table"
					>
						<thead className="bg-zinc-50">
							<tr>
								<th className={TH}>Time</th>
								<th className={TH}>Conversion</th>
								<th className={TH}>Advertiser</th>
								<th className={TH}>Reason</th>
								<th className={`${TH} text-right`}>Score</th>
								<th className={TH}>Status</th>
								<th className={`${TH} text-right`}>Amount</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{rows.map((row) => (
								<FeedRow key={row.id} row={row} />
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function FeedRow(props: { row: RealTimeConversionFraudDetectionRow }) {
	const { row } = props;
	const convId = row.conversion_id ?? row.id;
	const advertiser = row.advertiser ?? "—";
	const reason = (row.reason ?? "unknown") as FraudReason;
	const status = (row.status ?? "review") as FraudStatus;
	const score = typeof row.score === "number" ? row.score : undefined;
	const bucket = scoreBucket(score);
	const fraud = isFraudulent(row);

	return (
		<tr
			className="hover:bg-zinc-50"
			data-testid={`feed-row-${row.id}`}
			data-fraud={fraud ? "true" : "false"}
		>
			<td className={`${TD} text-zinc-600`}>{formatTs(row.created_at)}</td>
			<td className={`${TD} font-mono text-xs text-zinc-700`}>
				{String(convId)}
			</td>
			<td className={TD}>{String(advertiser)}</td>
			<td className={TD}>
				<ReasonBadge reason={reason} />
			</td>
			<td className={`${TD} text-right`}>
				<ScorePill score={score} bucket={bucket} />
			</td>
			<td className={TD}>
				<StatusPill status={status} />
			</td>
			<td className={`${TD} text-right text-zinc-700`}>
				{formatUsd(row.amount_cents)}
			</td>
		</tr>
	);
}

function ReasonBadge(props: { reason: FraudReason }) {
	const { reason } = props;
	const toneByReason: Record<FraudReason, ChipTone> = {
		dup_txn: "emerald",
		geo_spoof: "emerald",
		rapid_cvr: "emerald",
		ip_sharing: "emerald",
		ua_anomaly: "zinc",
		chargeback_risk: "red",
		unknown: "zinc",
	};
	const tone = toneByReason[reason];
	const palette: Record<ChipTone, string> = {
		emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
		red: "border-red-200 bg-red-50 text-red-700",
		zinc: "border-zinc-200 bg-zinc-50 text-zinc-600",
	};
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-[11px] font-medium ${palette[tone]}`}
		>
			{REASON_LABEL[reason]}
		</span>
	);
}

function ScorePill(props: {
	score: number | undefined;
	bucket: "high" | "mid" | "low";
}) {
	const { score, bucket } = props;
	if (typeof score !== "number") {
		return <span className="text-zinc-400">—</span>;
	}
	const tones: Record<typeof bucket, string> = {
		high: "border-red-500 bg-red-50 text-red-600",
		mid: "border-amber-300 bg-amber-50 text-amber-700",
		low: "border-emerald-200 bg-emerald-50 text-emerald-700",
	};
	return (
		<span
			className={`inline-flex min-w-[2.25rem] justify-center rounded-[6px] border px-2 py-0.5 text-xs font-semibold tabular-nums ${tones[bucket]}`}
			data-bucket={bucket}
			aria-label={`Fraud score ${score}`}
		>
			{Math.round(score)}
		</span>
	);
}

function StatusPill(props: { status: FraudStatus }) {
	const { status } = props;
	const palette: Record<FraudStatus, string> = {
		blocked: "border-red-200 bg-red-50 text-red-700",
		flagged: "border-amber-200 bg-amber-50 text-amber-700",
		review: "border-zinc-200 bg-zinc-50 text-zinc-700",
		clean: "border-emerald-200 bg-emerald-50 text-emerald-700",
	};
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-[11px] font-medium ${palette[status]}`}
		>
			{STATUS_LABEL[status]}
		</span>
	);
}

// ---------------------------------------------------------------------------
// Inline SVG icons (no emoji, no external deps)
// ---------------------------------------------------------------------------

function ShieldIcon() {
	return (
		<svg
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
		</svg>
	);
}

function FlagIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
			<line x1="4" y1="22" x2="4" y2="15" />
		</svg>
	);
}

function CopyIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
			<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
		</svg>
	);
}

function AlertIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
			<line x1="12" y1="9" x2="12" y2="13" />
			<line x1="12" y1="17" x2="12.01" y2="17" />
		</svg>
	);
}

export default RealTimeConversionFraudDetectionPage;
